import { useTranslate } from '@tolgee/react'
import { EmptyIndicator, tomTableColumnFilterPresets, TomTableEmptyCell } from '@tom-ui/ui'
import dayjs from 'dayjs'
import { MRT_ColumnDef } from 'material-react-table'
import { useMemo } from 'react'
import { ContainerJourneyReportColumnDto } from '../../stores/ContainerJourneyReportViewStore'

export const useContainerReportColumns = () => {
  const { t } = useTranslate()

  return useMemo<MRT_ColumnDef<ContainerJourneyReportColumnDto>[]>(
    () => [
      {
        id: 'containerNumber',
        header: t('containerNo', 'Container no.'),
        accessorKey: 'containerNumber',
        size: 150,
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        id: 'isoCode',
        header: t('isoCode', 'ISO'),
        accessorKey: 'isoCode',
        size: 80,
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        id: 'customerName',
        header: t('customer', 'Customer'),
        accessorKey: 'customerName',
        size: 160,
        Cell: ({ cell }) => cell.getValue<string>() || EmptyIndicator,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'entryDate',
        header: t('entryDate', 'Entry Date'),
        accessorKey: 'entryDate',
        size: 160,
        Cell: ({ cell }) =>
          cell.getValue<string>() ? dayjs(cell.getValue<string>()).format('L LT') : EmptyIndicator,
        ...tomTableColumnFilterPresets.dateOptional(),
      },
      {
        id: 'exitDate',
        header: t('exitDate', 'Exit Date'),
        accessorKey: 'exitDate',
        size: 160,
        Cell: ({ cell }) =>
          cell.getValue<string>() ? dayjs(cell.getValue<string>()).format('L LT') : EmptyIndicator,
        ...tomTableColumnFilterPresets.dateOptional(),
      },
      {
        id: 'entryMode',
        header: t('entryMode', 'Entry Mode'),
        accessorKey: 'entryMode',
        size: 120,
        Cell: ({ cell }) => cell.getValue<string>() || EmptyIndicator,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'exitMode',
        header: t('exitMode', 'Exit Mode'),
        accessorKey: 'exitMode',
        size: 120,
        Cell: ({ cell }) => cell.getValue<string>() || EmptyIndicator,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'entryRef',
        header: t('entryRef', 'Entry Ref.'),
        accessorKey: 'entryRef',
        size: 140,
        Cell: ({ cell }) => cell.getValue<string>() || EmptyIndicator,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'exitRef',
        header: t('exitRef', 'Exit Ref.'),
        accessorKey: 'exitRef',
        size: 140,
        Cell: ({ cell }) => cell.getValue<string>() || EmptyIndicator,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'bookingRef',
        header: t('bookingRef', 'Booking Ref.'),
        accessorKey: 'bookingRef',
        size: 140,
        Cell: ({ cell }) => cell.getValue<string>() || EmptyIndicator,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'loadStatus',
        header: t('loadStatus', 'Load Status'),
        accessorKey: 'loadStatus',
        size: 130,
        filterVariant: 'select',
        filterSelectOptions: [
          { text: t('full', 'Full'), value: 'Full' },
          { text: t('empty', 'Empty'), value: 'Empty' },
          { text: t('mixed', 'Mixed'), value: 'Mixed' },
        ],
        Cell: ({ cell }) => cell.getValue<string>() || EmptyIndicator,
      },
      {
        id: 'dangerousStatus',
        header: t('dangerousStatus', 'Dangerous'),
        accessorKey: 'dangerousStatus',
        size: 130,
        filterVariant: 'select',
        filterSelectOptions: [
          { text: t('dangerous', 'Dangerous'), value: 'Dangerous' },
          { text: t('notDangerous', 'Not Dangerous'), value: 'Not Dangerous' },
          { text: t('mixed', 'Mixed'), value: 'Mixed' },
        ],
        Cell: ({ cell }) => cell.getValue<string>() || EmptyIndicator,
      },
      {
        id: 'oogStatus',
        header: t('oogStatus', 'OOG'),
        accessorKey: 'oogStatus',
        size: 100,
        filterVariant: 'select',
        filterSelectOptions: [
          { text: t('oog', 'OOG'), value: 'OOG' },
          { text: t('notOog', 'Not OOG'), value: 'Not OOG' },
          { text: t('mixed', 'Mixed'), value: 'Mixed' },
        ],
        Cell: ({ cell }) => cell.getValue<string>() || EmptyIndicator,
      },
      {
        id: 'isReefer',
        header: t('isReefer', 'Reefer'),
        accessorFn: row => (row.isReefer ? 'Yes' : 'No'),
        size: 90,
        Cell: TomTableEmptyCell,
        ...tomTableColumnFilterPresets.yesNoSelect(),
      },
      {
        id: 'hasServices',
        header: t('hasServices', 'Has Services'),
        accessorFn: row => (row.hasServices ? 'Yes' : 'No'),
        size: 120,
        Cell: TomTableEmptyCell,
        ...tomTableColumnFilterPresets.yesNoSelect(),
      },
    ],
    [t],
  )
}
